import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import type { Session } from '@supabase/supabase-js';
import { useAuthStore } from './stores/authStore';
import { useLimitsStore } from './stores/limitsStore';
import { useUiStore } from './stores/uiStore';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { LimitReachedModal } from './components/LimitReachedModal/LimitReachedModal';
import { supabase } from './services/supabase';

const LoginPage = lazy(() => import('./pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() =>
  import('./pages/auth/RegisterPage').then(m => ({ default: m.RegisterPage }))
);
const CallbackPage = lazy(() =>
  import('./pages/auth/CallbackPage').then(m => ({ default: m.CallbackPage }))
);
const LandingPage = lazy(() =>
  import('./pages/LandingPage').then(m => ({ default: m.LandingPage }))
);
const DashboardPage = lazy(() =>
  import('./pages/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage }))
);
const CoachingPage = lazy(() =>
  import('./pages/coaching/CoachingPage').then(m => ({ default: m.CoachingPage }))
);
const CoachingOverlay = lazy(() =>
  import('./components/coaching/CoachingOverlay').then(m => ({ default: m.CoachingOverlay }))
);

export function App() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const setAuth = useAuthStore(s => s.setAuth);
  const clearAuth = useAuthStore(s => s.clearAuth);
  const setLoading = useAuthStore(s => s.setLoading);
  const modalLimitReached = useLimitsStore(s => s.modalLimitReached);
  const resetAt = useLimitsStore(s => s.resetAt);
  const closeLimitModal = useLimitsStore(s => s.closeLimitModal);
  const theme = useUiStore(s => s.theme);

  const isElectron = typeof window !== 'undefined' && !!window.electronAPI;

  useEffect(() => {
    // Only clear loading flag once to avoid redundant updates in StrictMode
    if (useAuthStore.getState().isLoading) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isMounted = true;

    const applySession = (session: Session) => {
      if (!isMounted) return;
      const existingUser = useAuthStore.getState().user;
      const userMetadata = session.user.user_metadata || {};
      setAuth(
        {
          id: session.user.id,
          email: session.user.email || existingUser?.email || '',
          firstName: existingUser?.firstName || userMetadata.firstName || 'User',
          lastName: existingUser?.lastName || userMetadata.lastName || '',
          tier: existingUser?.tier || 'FREE',
        },
        session.access_token,
        session.refresh_token || ''
      );
    };

    const initSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        applySession(data.session);
      }
    };

    initSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        applySession(session);
        return;
      }
      if (event === 'SIGNED_OUT') {
        clearAuth();
      }
    });

    return () => {
      isMounted = false;
      authListener.subscription.unsubscribe();
    };
  }, [setAuth, clearAuth]);

  useEffect(() => {
    const isDark = theme === 'dark';
    document.body.classList.remove('theme-dark', 'theme-light');
    document.body.classList.add(isDark ? 'theme-dark' : 'theme-light');
  }, [theme]);

  return (
    <BrowserRouter>
      <Suspense
        fallback={
          <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
            Loading...
          </div>
        }
      >
        <Routes>
          {/* Public landing (web-only) */}
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : isElectron ? (
                <Navigate to="/login" replace />
              ) : (
                <LandingPage />
              )
            }
          />

          {/* Public routes */}
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />}
          />
          <Route
            path="/register"
            element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPage />}
          />
          <Route
            path="/auth/callback"
            element={<CallbackPage />}
          />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Coaching routes */}
          <Route
            path="/coaching"
            element={
              <ProtectedRoute>
                <CoachingPage />
              </ProtectedRoute>
            }
          />
          <Route path="/overlay" element={<CoachingOverlay />} />
          <Route
            path="/mock-interview"
            element={
              <ProtectedRoute>
                <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
                  <p>Mock Interview - Coming in Phase 4</p>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
                  <p>Documents - Coming in Phase 5</p>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <ProtectedRoute>
                <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
                  <p>Subscription - Coming in Phase 7</p>
                </div>
              </ProtectedRoute>
            }
          />

          {/* Default redirect */}
          <Route
            path="*"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : isElectron ? (
                <Navigate to="/login" replace />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        </Routes>
      </Suspense>

      {/* Global Limit Reached Modal */}
      {modalLimitReached.isOpen && modalLimitReached.quotaType && resetAt && (
        <LimitReachedModal
          isOpen={modalLimitReached.isOpen}
          onClose={closeLimitModal}
          quotaType={modalLimitReached.quotaType}
          resetAt={resetAt}
        />
      )}
    </BrowserRouter>
  );
}
